import { useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { ScrollText, Copy, Check } from 'lucide-react';

export default function ContractClerk() {
  const [text, setText] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 50) return toast.error('Paste at least a few paragraphs of the contract.');
    setLoading(true); setSummary('');
    try {
      const { data } = await http.post('/ai/contract', { contract_text: text });
      setSummary(data.output);
      toast.success('Contract summarized.');
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };

  const copy = () => { navigator.clipboard.writeText(summary); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">Contract Clerk</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="contract-title">1-Page Contract Summaries</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Paste any contract. Your AI Employee extracts what matters: parties, price, terms, dates,
          contingencies, and any red flags.
        </p>
        <div
          className="mt-5 p-4 max-w-3xl text-xs"
          style={{ background: 'var(--parchment)', border: '1px solid var(--line)', borderRadius: 2, color: 'var(--ink-soft)', lineHeight: 1.6 }}
          data-testid="contract-privacy"
        >
          <div className="hq-eyebrow mb-2" style={{ color: 'var(--gold-soft)' }}>Privacy</div>
          Contract text you paste is stored in your private HomiHQ workspace and used only to generate your summary and audit log.
          It is not shared with other users. Only you and your logged-in team seats can access it. Delete any entry at any time
          from the AI Employee audit trail. HomiHQ uses Claude (Anthropic) via encrypted API for generation; content is not used
          to train third-party models.
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="hq-card p-8 space-y-5">
          <div>
            <label className="hq-label">Paste Contract Text</label>
            <textarea
              className="hq-input font-mono"
              rows={20}
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Paste the full contract text here — purchase agreement, listing agreement, addenda…"
              data-testid="contract-input"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs" style={{ color: 'var(--muted)' }}>{text.length.toLocaleString()} characters</div>
            <button className="hq-btn-primary" disabled={loading} data-testid="summarize-contract-btn">
              {loading ? 'Reading…' : <><ScrollText size={12} className="inline mr-2"/> Summarize</>}
            </button>
          </div>
        </form>

        <div className="hq-card p-8 min-h-[500px]">
          <div className="flex justify-between items-center mb-3">
            <span className="hq-eyebrow">Executive Summary</span>
            {summary && (
              <button onClick={copy} className="hq-btn-ghost" data-testid="copy-contract-btn">
                {copied ? <><Check size={12} className="inline mr-1"/> Copied</> : <><Copy size={12} className="inline mr-1"/> Copy</>}
              </button>
            )}
          </div>
          {loading && <div className="hq-shimmer h-96"></div>}
          {!loading && !summary && (
            <div className="text-center py-32" style={{ color: 'var(--muted)' }}>
              <ScrollText size={32} className="mx-auto mb-4" style={{ color: 'var(--gold)' }} />
              <div className="font-serif text-xl" style={{ color: 'var(--sage)' }}>Awaiting contract.</div>
            </div>
          )}
          {summary && (
            <>
              <pre className="whitespace-pre-wrap font-serif text-base hq-fade-in" style={{ color: 'var(--ink)', lineHeight: 1.7 }} data-testid="contract-summary">
                {summary}
              </pre>
              <div
                className="mt-6 p-4 text-xs"
                style={{ background: '#fbf1eb', border: '1px solid #e6c8bb', color: 'var(--danger)', borderRadius: 2, lineHeight: 1.6 }}
                data-testid="contract-disclaimer"
              >
                <strong className="uppercase tracking-widest" style={{ fontSize: '0.68rem' }}>Disclaimer</strong>
                <div className="mt-2" style={{ color: 'var(--ink)' }}>
                  AI-generated summary for informational purposes only. Not legal advice. Verify all dates,
                  terms, and obligations against the original agreement.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
